import { useState } from "react";
import { Link } from "react-router-dom";
import { youtubeThumbnailFallbackSrc } from "../lib/youtube";
import ArrowCta from "../components/home/ArrowCta";
import HeroCarousel from "../components/home/HeroCarousel";
import IconNav from "../components/home/IconNav";
import PageMeta from "../components/seo/PageMeta";
import { BLOG_POSTS } from "../data/blog";
import { useHomeFeaturedVideos } from "../hooks/useHomeFeaturedVideos";
import { useHomeLatestPhotos } from "../hooks/useHomeLatestPhotos";
import { buildPageTitle, buildWebPageJsonLd } from "../lib/seo";
import {
  SITE_DEFAULT_DESCRIPTION,
  SITE_HOME_TITLE,
  SITE_TAGLINE,
} from "../lib/siteConfig";
import { STORIES } from "../data/stories";
import { storyHref } from "../lib/storySeo";
import { uploadUrl } from "../lib/assets";
import "../styles/home.css";

function PlayButton() {
  return (
    <span className="plate-video__play" aria-hidden="true">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="rgba(244,241,234,0.9)">
        <polygon points="5,3 13,8 5,13" />
      </svg>
    </span>
  );
}

/** @param {{ thumbnailUrl?: string, youtubeVideoId?: string, title?: string }} props */
function FeaturedVideoCover({ thumbnailUrl, youtubeVideoId, title }) {
  const [src, setSrc] = useState(thumbnailUrl || "");

  if (!src) {
    return null;
  }

  return (
    <img
      className="plate-visual__cover"
      src={src}
      alt={title ? `${title} preview` : ""}
      loading="lazy"
      decoding="async"
      onError={() => {
        const next = youtubeThumbnailFallbackSrc(src, youtubeVideoId || "");
        if (next && next !== src) {
          setSrc(next);
        }
      }}
    />
  );
}

export default function HomePage() {
  const { photos: latestPhotos, loading: latestPhotosLoading } = useHomeLatestPhotos();
  const { videos: featuredVideos, loading: featuredVideosLoading } = useHomeFeaturedVideos();

  return (
    <div className="plate-page">
      <PageMeta
        title={SITE_HOME_TITLE}
        description={SITE_DEFAULT_DESCRIPTION}
        path="/"
        keywords={[
          "CJ Jayamaha",
          "photography",
          "wildlife photography",
          "travel stories",
          "conservation",
          "motorsport photography",
          "photo prints",
        ]}
        jsonLd={buildWebPageJsonLd({
          title: buildPageTitle(SITE_HOME_TITLE),
          description: SITE_TAGLINE,
          path: "/",
        })}
      />
      <HeroCarousel />
      <IconNav />

      <section className="plate-content">
        <Link className="plate-featured-story plate-featured-story--link" to={storyHref(STORIES[0])}>
          <div className="plate-featured-story__copy">
            <div className="plate-eyebrow">FEATURED STORY</div>
            <h2 className="plate-featured-story__title">Meeting the Last Two Northern White Rhinos</h2>
            <p className="plate-featured-story__blurb">
              A news headline. A flight to Kenya. A journey to stand beside the two
              remaining Northern White Rhinos on Earth. An experience that will stay with
              me forever.
            </p>
            <ArrowCta label="VIEW STORY" />
          </div>
          <div
            className="plate-featured-story__visual plate-visual plate-visual--story"
            style={{
              backgroundImage: `url('${uploadUrl("rhino-hero.jpg")}'), radial-gradient(120% 80% at 50% 30%, rgba(180,180,150,0.18), transparent 60%), linear-gradient(165deg, #2c3026 0%, #4a4633 46%, #6e6041 100%)`,
            }}
          >
            <span className="plate-visual__caption">[ white rhino, last light ]</span>
          </div>
        </Link>
      </section>

      <section className="plate-content plate-latest">
        <div className="plate-rule" />
        <div className="plate-section-header">
          <span className="plate-section-header__label">LATEST PHOTOGRAPHS</span>
          <Link to="/photography">
            <ArrowCta label="VIEW GALLERY" />
          </Link>
        </div>
        <div className="plate-latest__grid">
          {latestPhotos.map((photo) => {
            const focusX = photo.focusX ?? 50;
            const focusY = photo.focusY ?? 50;
            const captionLabel = photo.caption
              ? photo.caption.startsWith("[")
                ? photo.caption
                : `[ ${photo.caption} ]`
              : null;

            return (
              <div
                key={photo.slot}
                className="plate-visual plate-visual--photo"
                style={
                  photo.src
                    ? {
                        backgroundImage: `url('${photo.src}')`,
                        backgroundPosition: `${focusX}% ${focusY}%`,
                        boxShadow: "inset 0 0 70px rgba(0,0,0,0.4)",
                      }
                    : {
                        background: photo.background,
                        boxShadow: photo.shadow,
                      }
                }
                aria-busy={latestPhotosLoading}
              >
                {captionLabel ? (
                  <span className="plate-visual__caption plate-visual__caption--small">
                    {captionLabel}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>

      <section className="plate-content plate-videos">
        <div className="plate-section-header plate-section-header--videos">
          <span className="plate-section-header__label">FEATURED VIDEO</span>
          <Link to="/videos">
            <ArrowCta label="VIEW ALL VIDEOS" />
          </Link>
        </div>
        <div className="plate-videos__grid">
          {featuredVideos.map((video) => {
            const content = (
              <>
                {video.thumbnailUrl ? (
                  <FeaturedVideoCover
                    thumbnailUrl={video.thumbnailUrl}
                    youtubeVideoId={video.youtubeVideoId}
                    title={video.title}
                  />
                ) : null}
                <PlayButton />
                <span className="plate-visual__caption">{video.caption}</span>
              </>
            );

            const style = video.thumbnailUrl ? undefined : { background: video.background };

            if (video.href) {
              return (
                <Link
                  key={video.slot}
                  to={video.href}
                  className="plate-visual plate-visual--video plate-visual--video-link"
                  style={style}
                  aria-busy={featuredVideosLoading}
                >
                  {content}
                </Link>
              );
            }

            return (
              <div
                key={video.slot}
                className="plate-visual plate-visual--video"
                style={style}
                aria-busy={featuredVideosLoading}
              >
                {content}
              </div>
            );
          })}
        </div>
      </section>

      <section className="plate-content plate-blog">
        <div className="plate-section-header plate-section-header--blog">
          <span className="plate-section-header__label">FROM THE BLOG</span>
          <Link to="/blog">
            <ArrowCta label="VIEW ALL POSTS" />
          </Link>
        </div>
        <div className="plate-blog__grid">
          {BLOG_POSTS.slice(0, 3).map((post) => (
            <article key={post.id} className="plate-blog__row">
              <div
                className="plate-blog__thumb"
                style={{
                  backgroundImage: `url('${uploadUrl(post.img)}')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <div>
                <time className="plate-blog__date">{post.date}</time>
                <Link to={`/blog/${post.id}`} className="plate-blog__title">
                  {post.title}
                </Link>
                <svg
                  className="plate-blog__arrow"
                  width="34"
                  height="8"
                  viewBox="0 0 40 8"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  aria-hidden="true"
                >
                  <line x1="0" y1="4" x2="38" y2="4" />
                  <path d="M34 1 L39 4 L34 7" />
                </svg>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
